'use client';

import React from 'react';
import { Button } from '@ui';

export interface PaginationNavigationProps {
  currentPage: number;
  totalPages: number;
  pagerNumbers: Array<number | '…'>;
  goto: (page: number) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  id?: string;
  className?: string;
  'aria-label'?: string;
}

const PaginationNavigation = ({
  currentPage,
  totalPages,
  pagerNumbers,
  goto,
  onKeyDown,
  id = 'paginationNav',
  className,
  'aria-label': ariaLabel = 'Pagination',
}: PaginationNavigationProps) => {
  return (
    <nav
      className={className ? `pagination ${className}` : 'pagination'}
      id={id}
      role='navigation'
      aria-label={ariaLabel}
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <Button
        className='btn-outline-tertiary btn-sm pagi-btn'
        data-first
        aria-label='First page'
        onClick={() => goto(1)}
        disabled={currentPage <= 1}
        type='button'
        label='«'
      />
      <Button
        className='btn-outline-tertiary btn-sm pagi-btn'
        data-prev
        aria-label='Previous page'
        onClick={() => goto(currentPage - 1)}
        disabled={currentPage <= 1}
        type='button'
        label='‹'
      />

      <div className='pagi-numbers' aria-hidden='false'>
        {pagerNumbers.map((n, i) =>
          n === '…' ? (
            <span
              key={`ellipsis-${i}`}
              className='pagi-number'
              aria-hidden='true'
              style={{ pointerEvents: 'none' }}
            >
              …
            </span>
          ) : (
            <Button
              key={`page-${n}-${i}`}
              className='btn-outline-tertiary btn-sm pagi-number'
              aria-current={n === currentPage ? 'page' : undefined}
              data-page={n}
              aria-label={`Page ${n}`}
              onClick={() => goto(Number(n))}
              type='button'
              label={String(n)}
            />
          )
        )}
      </div>

      <Button
        className='btn-outline-tertiary btn-sm pagi-btn'
        data-next
        aria-label='Next page'
        onClick={() => goto(currentPage + 1)}
        disabled={currentPage >= totalPages}
        type='button'
        label='›'
      />
      <Button
        className='btn-outline-tertiary btn-sm pagi-btn'
        data-last
        aria-label='Last page'
        onClick={() => goto(totalPages)}
        disabled={currentPage >= totalPages}
        type='button'
        label='»'
      />
    </nav>
  );
};

export default PaginationNavigation;
